import {
  appSetupReadSchema,
  onboardingSessionDataSchema,
  careAppointmentDataSchema,
  careAppointmentSlotsDataSchema,
  checkInDataSchema,
  companySuggestionsViewSchema,
  consentDocumentPreviewDataSchema,
  conversionAttributionForRequest,
  hdhpNoticeDataSchema,
  onboardingUsageConfigSchema,
  MEMBER_CARE_PREFERENCES_PATH,
  type PutMemberCarePreferencesRequest,
  type OnboardingSessionData,
  signupClientIdSchema,
  signupResumeViewSchema,
  signupSponsorSchema,
} from '@galapagos/shared';
import { ONBOARDING_FLOW_ID, ONBOARDING_RELEASE_VERSION } from '@galapagos/onboarding-flow';
import { meDataSchema } from '@galapagos/shared/api/auth';
import {
  SIGNUP_BASELINE_CONTENT,
  SIGNUP_PROGRAM_OPTIONS,
  SIGNUP_QUIZ_CONTENT,
  signupBaselineContentSchema,
  signupProgramOptionsSchema,
  signupQuizContentSchema,
} from '@galapagos/shared/registration';
import { z } from 'zod';
import { readConversionAttribution } from '@/features/attribution';
import { buildDownloadHandoff } from '../../download/download-links';
import { apiGet, apiPost, apiPut, type ApiResult } from '@/services/api';
import {
  SignupNotImplementedError,
  SignupRequestError,
  type SignupApi,
  type SignupApiMethod,
} from './signup-api';

export const CHECK_INS_BATCH_PATH = '/api/v1/check-ins/batch';
export const CARE_SLOTS_PATH = '/api/v1/care/appointments/slots';
export const CARE_APPOINTMENTS_PATH = '/api/v1/care/appointments';
export const HDHP_NOTICE_PATH = '/api/v1/members/me/hdhp-notice';
export const AUTH_ME_PATH = '/api/v1/auth/me';
export const SIGNUP_RESUME_PATH = '/api/v1/members/me/signup-resume';
export const ONBOARDING_USAGE_CONFIG_PATH = '/api/v1/onboarding/sessions/config/usage';

const scopedCompanySearchPath = (query: string, parentRef: string): string =>
  `/api/v1/eligibility/companies?query=${encodeURIComponent(query)}&parentRef=${encodeURIComponent(parentRef)}`;

const sponsorPath = (clientId: string): string =>
  `/api/v1/eligibility/sponsors/${encodeURIComponent(clientId)}`;

const consentPreviewPath = (consentType: string): string =>
  `/api/v1/members/me/consents/${encodeURIComponent(consentType)}/document/preview`;

const checkInBatchDataSchema = z.array(checkInDataSchema);

// The OTP, onboarding-session and registration calls live in the auth,
// onboarding and registration features. Loading them on first use keeps them
// out of the signup pages' first load; the service code is reused, not copied.
const authFeature = () => import('@/features/auth');
const onboardingFeature = () => import('@/features/onboarding');
const registrationFeature = () => import('@/features/registration');

async function unwrap<T>(pending: Promise<ApiResult<T>>): Promise<T> {
  const result = await pending;
  if (!result.ok) throw SignupRequestError.fromApiError(result.error);
  return result.data;
}

// The OTP service throws its own typed error; the seam hands screens one error
// type for every call, so the server's code and wait hint carry across.
async function otpCall<T>(
  call: (auth: Awaited<ReturnType<typeof authFeature>>) => Promise<T>,
): Promise<T> {
  const auth = await authFeature();
  try {
    return await call(auth);
  } catch (error) {
    if (error instanceof auth.OtpRequestError) {
      throw new SignupRequestError(error.message, {
        kind: error.kind,
        httpStatus: error.httpStatus,
        code: error.code,
        retryAfterSeconds: error.retryAfterSeconds,
      });
    }
    throw error;
  }
}

// One route saves every care preference; each method sends only its own field,
// and the server leaves the others as stored. The callers ignore what the save
// returns, so the response is not held to this bundle's vocabulary: a value the
// API learns later must not turn a save that worked into a failure (ADR-135).
const savedCarePreferencesSchema = z.unknown();

async function saveCarePreferences(body: PutMemberCarePreferencesRequest): Promise<void> {
  await unwrap(apiPut(MEMBER_CARE_PREFERENCES_PATH, body, savedCarePreferencesSchema));
}

function notImplemented(method: SignupApiMethod): Promise<never> {
  return Promise.reject(new SignupNotImplementedError(method));
}

/**
 * Serves fixed content the bundle already carries, checked by its owning
 * schema as a response would be, so a bad edit to the content fails the same
 * way a bad response does instead of reaching a screen half-formed.
 */
export function serveContent<S extends z.ZodTypeAny>(
  schema: S,
  content: unknown,
): Promise<z.output<S>> {
  const parsed = schema.safeParse(content);
  return parsed.success
    ? Promise.resolve(parsed.data)
    : Promise.reject(
        new SignupRequestError('The signup content did not match its schema', { kind: 'parse' }),
      );
}

function slotsPath(query: Record<string, string | number | undefined> = {}): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined) params.set(key, String(value));
  }
  const search = params.toString();
  return search ? `${CARE_SLOTS_PATH}?${search}` : CARE_SLOTS_PATH;
}

/**
 * The seam over the Galapagos API. Calls web already makes go through the
 * existing service functions, so their attribution and error behaviour stay in
 * one place; endpoints mobile uses but web did not yet are called here with
 * their shared schemas. Fixed signup content is served from the bundle. Calls
 * with no backend reject without a request.
 */
export interface SignupOnboardingSourceOwner {
  readonly sourceId: string | undefined;
  isCurrent(): boolean;
  bindSourceId(id: string): string | undefined;
}

export interface RealSignupApiOptions {
  captureOnboardingSource?: () => SignupOnboardingSourceOwner;
}

export function createRealSignupApi(options: RealSignupApiOptions = {}): SignupApi {
  return {
    // Landing pages link a sub-client as client_id=<parent>&sub_client_id=<own
    // id>, and a former sub-client now has its own sponsor record under that
    // id, so a well-formed sub-client id names the sponsor when the link carries
    // one; a malformed one is ignored so it cannot hide a good client_id. A
    // link with neither names no sponsor; answer as the API does for an
    // unknown one.
    resolveSponsor: ({ clientId, subClientId }) => {
      const sponsorId = signupClientIdSchema.safeParse(subClientId).success
        ? subClientId
        : clientId;
      return sponsorId === undefined || sponsorId === ''
        ? Promise.reject(new SignupRequestError('not found', { code: 'not_found' }))
        : unwrap(apiGet(sponsorPath(sponsorId), signupSponsorSchema));
    },
    // Program options, quiz questions and baseline wording are fixed structure
    // kept in the shared registration package (their words come from the
    // signup catalog); the full option list is served and the substance step
    // narrows it to what the sponsor allows.
    getProgramOptions: () => serveContent(signupProgramOptionsSchema, SIGNUP_PROGRAM_OPTIONS),
    initiateOtp: (input) => otpCall((auth) => auth.initiateOtp(input)),
    verifyOtp: (input) => otpCall((auth) => auth.verifyOtp(input)),
    getRegistrationProfile: async () =>
      unwrap((await registrationFeature()).getRegistrationCompletion()),
    getSignedInContact: async () => {
      const me = await unwrap(apiGet(AUTH_ME_PATH, meDataSchema));
      return { email: me.email, phone: me.phone_number };
    },
    getSignupResume: () => unwrap(apiGet(SIGNUP_RESUME_PATH, signupResumeViewSchema)),
    submitEligibility: async (request) =>
      unwrap((await registrationFeature()).submitEligibilityCheck(request)),
    getEligibilityStatus: async () => unwrap((await registrationFeature()).getEligibilityStatus()),
    searchEmployers: async (query, scope) =>
      scope
        ? unwrap(
            apiGet(scopedCompanySearchPath(query, scope.parentRef), companySuggestionsViewSchema),
          )
        : unwrap((await registrationFeature()).searchCompanies(query)),
    suggestAddresses: async (query) =>
      unwrap((await registrationFeature()).suggestAddresses(query)),
    saveRegistrationProfile: async (write) =>
      unwrap((await registrationFeature()).completeRegistrationProfile(write)),
    getHdhpNotice: () => unwrap(apiGet(HDHP_NOTICE_PATH, hdhpNoticeDataSchema)),
    saveLanguagePreference: ({ language }) =>
      saveCarePreferences({ callLanguage: { language, source: 'member' } }),
    saveContactMode: ({ contactMode }) => saveCarePreferences({ contactMode }),
    saveAppLanguageDefault: ({ language }) =>
      saveCarePreferences({ callLanguage: { language, source: 'app_language' } }),
    previewConsentDocument: (consentType) =>
      unwrap(apiPost(consentPreviewPath(consentType), {}, consentDocumentPreviewDataSchema)),
    // Unused: the calls-and-texts agreement and the Part 2 release are both
    // recorded at OTP verify (verifyOtp's consent block); no screen calls this.
    captureConsent: () => notImplemented('captureConsent'),
    getQuizContent: () => serveContent(signupQuizContentSchema, SIGNUP_QUIZ_CONTENT),
    getOnboardingSession: async (flowId) =>
      unwrap((await onboardingFeature()).getOnboardingSession(flowId)),
    // Every write on the default flow names web onboarding's version, so the
    // first write fixes the session's version, as the onboarding hook does.
    answerOnboarding: async (flowId, body) => {
      if (flowId === ONBOARDING_FLOW_ID && body.finalize) {
        const owner = options.captureOnboardingSource?.();
        if (!owner)
          throw new SignupRequestError('Questionnaire owner unavailable', {
            kind: 'http',
            httpStatus: 503,
          });
        let questionnaireSessionId = owner.sourceId;
        if (!questionnaireSessionId) {
          const assertCurrent = () => {
            if (!owner.isCurrent())
              throw new SignupRequestError('Signup session changed', {
                kind: 'http',
                httpStatus: 409,
              });
          };
          const appendUnreserved = async () => {
            const onboarding = await onboardingFeature();
            assertCurrent();
            return unwrap(
              onboarding.appendOnboardingAnswers(flowId, {
                ...body,
                flowVersion: ONBOARDING_RELEASE_VERSION.web,
              }),
            );
          };
          let prior: OnboardingSessionData | undefined;
          try {
            prior = await unwrap((await onboardingFeature()).getOnboardingSession(flowId));
          } catch (error) {
            if (
              !(error instanceof SignupRequestError) ||
              error.httpStatus !== 404 ||
              error.code !== 'not_found'
            )
              throw error;
          }
          assertCurrent();
          // Only a source already bound by this tab can be retried as a
          // successful completion. Preserve the ordinary session_completed
          // response for a fresh submission against a completed questionnaire.
          if (prior?.status === 'completed') return appendUnreserved();
          let sourceId: string | undefined;
          try {
            const setup = await unwrap(
              apiPost(
                '/api/v1/onboarding/sessions/app-setup/commands',
                {
                  operation: 'initialize_web',
                  flowId,
                  requestKey: crypto.randomUUID(),
                },
                appSetupReadSchema,
              ),
            );
            sourceId = setup.questionnaireSessionId;
          } catch (error) {
            if (
              !(error instanceof SignupRequestError) ||
              error.httpStatus !== 409 ||
              !['setup_prerequisite', 'setup_conflict'].includes(error.code ?? '')
            )
              throw error;
            // Migrated and other non-web-owned questionnaires cannot reserve
            // post-web setup. Keep their existing completion route instead of
            // making that new eligibility prerequisite block every retry.
            return appendUnreserved();
          }
          assertCurrent();
          if (!sourceId)
            throw new SignupRequestError('Questionnaire source unavailable', {
              kind: 'http',
              httpStatus: 503,
            });
          if (prior && prior.id !== sourceId)
            throw new SignupRequestError('Questionnaire source changed', {
              kind: 'http',
              httpStatus: 409,
            });
          questionnaireSessionId = owner.bindSourceId(sourceId);
          if (questionnaireSessionId !== sourceId)
            throw new SignupRequestError('Questionnaire source changed', {
              kind: 'http',
              httpStatus: 409,
            });
        }
        return unwrap(
          apiPost(
            '/api/v1/onboarding/sessions/app-setup/questionnaire-completion',
            {
              questionnaireSessionId,
              answers: body.answers,
            },
            onboardingSessionDataSchema,
          ),
        );
      }
      return unwrap(
        (await onboardingFeature()).appendOnboardingAnswers(flowId, {
          ...body,
          ...(flowId === ONBOARDING_FLOW_ID && { flowVersion: ONBOARDING_RELEASE_VERSION.web }),
        }),
      );
    },
    getBaselineContent: () => serveContent(signupBaselineContentSchema, SIGNUP_BASELINE_CONTENT),
    getOnboardingUsageConfig: () =>
      unwrap(apiGet(ONBOARDING_USAGE_CONFIG_PATH, onboardingUsageConfigSchema)),
    submitCheckIns: (request) =>
      unwrap(apiPost(CHECK_INS_BATCH_PATH, request, checkInBatchDataSchema)),
    getBookingSlots: (query) => unwrap(apiGet(slotsPath(query), careAppointmentSlotsDataSchema)),
    bookAppointment: (request) => {
      // A booked call is a designated conversion: a live landing capture rides it.
      const attribution = readConversionAttribution();
      const body = attribution
        ? { ...request, attribution: conversionAttributionForRequest(attribution) }
        : request;
      return unwrap(apiPost(CARE_APPOINTMENTS_PATH, body, careAppointmentDataSchema));
    },
    // The store links need no backend: fixed listings plus the member's own
    // campaign, built in the browser as the legacy signup builds them.
    getDownloadHandoff: async (request) =>
      buildDownloadHandoff(request?.campaign ?? readConversionAttribution(), request?.placement),
  };
}
